// Клиент админских endpoint-ов. Доступ проверяет сервер на каждом запросе —
// здесь только форма данных.
import { request } from '../auth/request';
import type { ApiResult } from '../auth/request';
import type { AuthUser, UserLimits, UserRole, UserStatus } from '../auth/api';
import type { SupportFileInput, SupportMessage } from '../support/api';
import type { TripMeta } from '../trips/api';
import type { AppState } from '../types';

export interface AdminUser extends AuthUser {
  /** Сохранённое состояние пользователя; null — он ещё ничего не сохранял. */
  workspace: { rev: number; updatedAt: number; size: number } | null;
}

export interface UsersPage {
  users: AdminUser[];
  total: number;
  limit: number;
  offset: number;
}

/** Строка списка всех поездок: та же поездка, но глазами администратора. */
export interface AdminTrip extends Omit<TripMeta, 'role'> {
  /** Владелец. null — поездка осталась от времён, когда владельцев не было. */
  ownerId: string | null;
  /** Владелец удалил аккаунт: поездка жива, но никому не доступна. */
  ownerDeleted: boolean;
}

export interface TripsPage {
  trips: AdminTrip[];
  total: number;
  limit: number;
  offset: number;
}

export interface TripStateView {
  trip: AdminTrip;
  exists: boolean;
  rev: number;
  updatedAt: number | null;
  state: AppState | null;
}

export interface AdminStats {
  users: number;
  verified: number;
  blocked: number;
  admins: number;
  newWeek: number;
  newMonth: number;
  activeWeek: number;
  activeMonth: number;
  workspaces: number;
  tripsShared: number;
  tripsPublished: number;
  snapshots: number;
  sessions: number;
  stateBytes: number;
}

export interface UserStateView {
  user: AdminUser;
  exists: boolean;
  rev: number;
  updatedAt: number | null;
  state: AppState | null;
}

export function listUsers(params: {
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiResult<UsersPage>> {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.limit != null) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  return request(`/api/admin/users?${query.toString()}`);
}

export function fetchStats(): Promise<ApiResult<{ stats: AdminStats }>> {
  return request('/api/admin/stats');
}

export function patchUser(
  id: string,
  patch: { role?: UserRole; status?: UserStatus; limits?: UserLimits | null },
): Promise<ApiResult<{ user: AdminUser }>> {
  return request(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', json: patch });
}

export function deleteUser(id: string): Promise<ApiResult<void>> {
  return request(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function fetchUserState(id: string): Promise<ApiResult<UserStateView>> {
  return request(`/api/admin/users/${encodeURIComponent(id)}/state`);
}

export function listAllTrips(params: {
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiResult<TripsPage>> {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.limit != null) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  return request(`/api/admin/trips?${query.toString()}`);
}

export function fetchTripState(id: string): Promise<ApiResult<TripStateView>> {
  return request(`/api/admin/trips/${encodeURIComponent(id)}/state`);
}

// ──────────────────────────── Журнал сервера ────────────────────────────

export type LogLevel = 'info' | 'warn' | 'error';

/** Строка списка — без тяжёлого поля details; оно тянется отдельно по id. */
export interface AdminLogRow {
  id: number;
  ts: number;
  level: LogLevel;
  scope: string;
  message: string;
  hasDetails: boolean;
}

export interface LogsPage {
  logs: AdminLogRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminLogDetail extends Omit<AdminLogRow, 'hasDetails'> {
  /** JSON-строка с телом записи (промпт, ответ модели и т.п.) или null. */
  details: string | null;
}

export function listLogs(params: {
  levels?: LogLevel[];
  limit?: number;
  offset?: number;
}): Promise<ApiResult<LogsPage>> {
  const query = new URLSearchParams();
  if (params.levels?.length) query.set('levels', params.levels.join(','));
  if (params.limit != null) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  return request(`/api/admin/logs?${query.toString()}`);
}

export function fetchLog(id: number): Promise<ApiResult<{ log: AdminLogDetail }>> {
  return request(`/api/admin/logs/${id}`);
}

export function clearLogs(): Promise<ApiResult<void>> {
  return request('/api/admin/logs', { method: 'DELETE' });
}

// ──────────────────────────── Саппорт-чат ────────────────────────────────

/** Строка списка диалогов: один пользователь — один чат. */
export interface AdminChat {
  userId: string;
  email: string;
  name: string;
  userDeleted: boolean;
  /** Язык интерфейса пользователя ('ru', 'en', …); пусто — неизвестен. */
  lang: string;
  unread: number;
  messages: number;
  lastMessage: SupportMessage | null;
}

export interface ChatsPage {
  chats: AdminChat[];
  total: number;
  /** Неотвеченных сообщений во всех диалогах — счётчик вкладки «Чаты». */
  unreadTotal: number;
  limit: number;
  offset: number;
}

export function listChats(params: {
  limit?: number;
  offset?: number;
}): Promise<ApiResult<ChatsPage>> {
  const query = new URLSearchParams();
  if (params.limit != null) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  return request(`/api/admin/support/chats?${query.toString()}`);
}

export function fetchChat(
  userId: string,
  afterId = 0,
  signal?: AbortSignal,
): Promise<
  ApiResult<{ user: AdminUser; lang: string; userAgent: string; messages: SupportMessage[] }>
> {
  const query = afterId > 0 ? `?after=${afterId}` : '';
  return request(`/api/admin/support/chats/${encodeURIComponent(userId)}${query}`, {
    method: 'GET',
    signal,
  });
}

/** Завершить разговор: история уходит в архив, видимый только админке. */
export function closeChat(userId: string): Promise<ApiResult<{ closed: number }>> {
  return request(`/api/admin/support/chats/${encodeURIComponent(userId)}/close`, { method: 'POST' });
}

export function sendChatMessage(
  userId: string,
  text: string,
  file?: SupportFileInput,
): Promise<ApiResult<{ message: SupportMessage }>> {
  return request(`/api/admin/support/chats/${encodeURIComponent(userId)}`, {
    method: 'POST',
    json: { text, ...(file ? { file } : {}) },
  });
}

export function deleteChat(userId: string): Promise<ApiResult<void>> {
  return request(`/api/admin/support/chats/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}
